/**
 * report.ts
 *
 * Pure helpers for Title Analysis reports.
 *
 * - `downloadFileName`: generate a safe filename for report downloads.
 * - `csvFileName`: the same name with a `.csv` extension (see `reportCsv.ts`).
 *
 * Requirements: 18.20
 */

/**
 * Generate a safe filename for downloading a Title Analysis report.
 *
 * The filename is constructed as:
 *   `title-analysis-{loanNumber}-{analysisDate}.json`
 *
 * Characters outside the set `[A-Za-z0-9._-]` are replaced with underscores.
 * If loanNumber is missing, it is omitted from the filename.
 *
 * @param loanNumber The loan number (may be null/undefined).
 * @param analysisDate The analysis date in YYYY-MM-DD format.
 * @returns A safe filename that can be used for download.
 *
 * Requirements: 18.20 (replaces characters outside [A-Za-z0-9._-] with _).
 */
export function downloadFileName(
  loanNumber: string | null | undefined,
  analysisDate: string
): string {
  const safeLoan = loanNumber ? sanitizeFilenamePart(loanNumber) : '';
  const safeDate = sanitizeFilenamePart(analysisDate);

  let name = 'title-analysis';
  if (safeLoan) {
    name += `-${safeLoan}`;
  }
  if (safeDate) {
    name += `-${safeDate}`;
  }
  return `${name}.json`;
}

/** Same as `downloadFileName`, with a `.csv` extension. */
export function csvFileName(loanNumber: string | null | undefined, analysisDate: string): string {
  return downloadFileName(loanNumber, analysisDate).replace(/\.json$/, '.csv');
}

/**
 * Replace characters outside `[A-Za-z0-9._-]` with underscores.
 *
 * @param part A string that should become part of a filename.
 * @returns A sanitized string where every disallowed character is replaced with `_`.
 */
function sanitizeFilenamePart(part: string): string {
  return part.replace(/[^A-Za-z0-9._-]/g, '_');
}
